import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { TrendingUp, TrendingDown, RefreshCw } from 'lucide-react';
import { BACKEND_URL } from '../config';

const ForexPage = () => {
  const navigate = useNavigate();
  const [forexData, setForexData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const fetchForexData = async (showLoading = false) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      const response = await fetch(`${BACKEND_URL}/api/forex/rates`);
      const data = await response.json();
      
      // Map the backend data to our display format
      const processedData = data.rates.map(item => {
        return {
          from: item.pair.split('/')[0],
          to: item.pair.split('/')[1],
          name: `${item.name} / ${item.pair.split('/')[1]}`,
          symbol: getSymbol(item.pair.split('/')[0]),
          rate: item.rate,
          change24h: item.change,
          high24h: item.rate * 1.002,
          low24h: item.rate * 0.998
        };
      });
      
      setForexData(processedData);
      setLastUpdate(new Date());
      setLoading(false);
    } catch (error) {
      console.error('Error fetching forex data:', error);
      setLoading(false);
    }
  };

  const getSymbol = (currency) => {
    const symbols = {
      'EUR': '€',
      'GBP': '£',
      'JPY': '¥',
      'AUD': 'A$',
      'CAD': 'C$',
      'CHF': 'Fr',
      'CNY': '¥',
      'NZD': 'NZ$',
      'USD': '$'
    };
    return symbols[currency] || currency;
  };

  // Functions removed

  useEffect(() => {
    // Initial load with loading spinner
    fetchForexData(true);
    
    // Auto-refresh every 30 seconds without loading spinner
    const interval = setInterval(() => {
      fetchForexData(false);
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const formatRate = (rate) => {
    return rate.toFixed(4);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="relative bg-gradient-to-br from-[#0d6571] to-[#0d6571]/80 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-5xl font-bold text-white mb-4">
            Foreign Exchange Market
          </h1>
          <p className="text-xl text-white/90">
            Real-time forex rates and currency pair data
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Controls */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-gray-600">
              <h2 className="text-2xl font-bold text-gray-900">Major Currency Pairs</h2>
              <p className="text-sm mt-1">Live exchange rates updating every 30 seconds</p>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600">
                Last updated: {lastUpdate.toLocaleTimeString()}
              </div>
              <button
                onClick={() => fetchForexData(false)}
                className="flex items-center gap-2 px-4 py-2 bg-[#0d6571] text-white rounded-lg hover:bg-[#0d6571]/90 transition-colors"
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Forex Cards Grid */}
        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#0d6571] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading forex data...</p>
          </div>
        ) : (
          <>
            {/* DXY Card */}
            <div className="bg-gradient-to-br from-[#0d6571] to-[#0d6571]/80 rounded-xl shadow-lg p-8 mb-8 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold mb-2">US Dollar Index (DXY)</h3>
                  <div className="text-5xl font-bold mb-2">104.25</div>
                  <div className="text-sm opacity-80 mt-2">
                    Live Index Value
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm opacity-80">Typical Range</div>
                  <div className="text-2xl font-semibold">102 - 106</div>
                  <div className="text-xs opacity-60 mt-2">Based on recent volatility</div>
                </div>
              </div>
            </div>

            {/* Currency Pairs Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {forexData.map((pair, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">
                        {pair.from}/{pair.to}
                      </h3>
                      <p className="text-sm text-gray-500">{pair.name}</p>
                    </div>
                    <div className="bg-[#0d6571]/10 p-3 rounded-lg">
                      <span className="text-2xl font-bold text-[#0d6571]">{pair.symbol}</span>
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="text-4xl font-bold text-gray-900">
                      {formatRate(pair.rate)}
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-sm font-semibold text-[#0d6571]">Live Rate</span>
                      <div className={`flex items-center gap-1 text-sm font-semibold ${
                        pair.change24h >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {pair.change24h >= 0 ? (
                          <TrendingUp className="w-4 h-4" />
                        ) : (
                          <TrendingDown className="w-4 h-4" />
                        )}
                        <span>{pair.change24h >= 0 ? '+' : ''}{pair.change24h.toFixed(2)}%</span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                    <div>
                      <div className="text-xs text-gray-500 uppercase">24h High</div>
                      <div className="text-lg font-semibold text-gray-900">
                        {formatRate(pair.high24h)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 uppercase">24h Low</div>
                      <div className="text-lg font-semibold text-gray-900">
                        {formatRate(pair.low24h)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Market Information */}
        <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Market Information</h2>
          <p className="text-gray-600 mb-4">
            Data provided via Yahoo Finance. Rates update automatically every 30 seconds showing live exchange rates with daily change percentages.
            Click the refresh button to manually update the data.
          </p>
          <p className="text-sm text-gray-500">
            <strong>Note:</strong> The rates shown are current live rates from Yahoo Finance. High/Low values are estimates based on typical forex volatility. 
            DXY (US Dollar Index) is shown as a reference value.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ForexPage;
